import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { useUser } from './UserContext';

// Define interfaces for course prerequisite data
interface CoursePrerequisite {
  Prereq: string; // e.g., "None", "ADA10,ADA20"
}

interface CoursePrerequisites {
  [courseCode: string]: CoursePrerequisite;
}

interface CoursePrerequisiteContextType {
  coursePrerequisites: CoursePrerequisites;
  loading: boolean;
  error: string | null;
  fetchCoursePrerequisites: () => Promise<void>;
  getPrerequisitesForCourse: (courseCode: string) => string[];
}

const CoursePrerequisiteContext = createContext<CoursePrerequisiteContextType | undefined>(undefined);

interface CoursePrerequisiteProviderProps {
  children: ReactNode;
}

export const CoursePrerequisiteProvider: React.FC<CoursePrerequisiteProviderProps> = ({ children }) => {
  const [coursePrerequisites, setCoursePrerequisites] = useState<CoursePrerequisites>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchCoursePrerequisites = async () => {
    // Don't fetch if user is not authenticated
    if (!user) {
      setLoading(false);
      setCoursePrerequisites({});
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching course prerequisites from Firebase...');
      const db = getDatabase();
      const coursePrerequisitesRef = ref(db, 'highSchoolPrereq_new');
      const snapshot = await get(coursePrerequisitesRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('Course prerequisites fetched:', Object.keys(data).length, 'entries');
        setCoursePrerequisites(data);
      } else {
        console.log('No course prerequisites found in database');
        setCoursePrerequisites({});
      }
    } catch (err) {
      console.error('Error fetching course prerequisites:', err);
      setError('Failed to fetch course prerequisites');
    } finally {
      setLoading(false);
    }
  };

  const getPrerequisitesForCourse = (courseCode: string): string[] => {
    const courseData = coursePrerequisites[courseCode];
    if (!courseData || !courseData.Prereq) {
      return []; // No prerequisites found
    }

    const prereqString = courseData.Prereq.trim();
    
    // Handle "None" case
    if (prereqString === "None") {
      return [];
    }

    // Parse comma-separated prerequisites
    return prereqString.split(',').map(prereq => prereq.trim()).filter(prereq => prereq.length > 0);
  };

  useEffect(() => {
    fetchCoursePrerequisites();
  }, [user]);

  const value: CoursePrerequisiteContextType = {
    coursePrerequisites,
    loading,
    error,
    fetchCoursePrerequisites,
    getPrerequisitesForCourse,
  };

  return (
    <CoursePrerequisiteContext.Provider value={value}>
      {children}
    </CoursePrerequisiteContext.Provider>
  );
};

export const useCoursePrerequisites = (): CoursePrerequisiteContextType => {
  const context = useContext(CoursePrerequisiteContext);
  if (context === undefined) {
    throw new Error('useCoursePrerequisites must be used within a CoursePrerequisiteProvider');
  }
  return context;
};
